'use client';

import { forwardRef, ReactNode, useState } from 'react';
import { faCopy, faShare } from '@fortawesome/pro-light-svg-icons';
import { VariantProps, tv } from 'tailwind-variants';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { Laboratory, Partner, Professional } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { config } from '@/config/config';
import { useToast } from '@/components/ui/use-toast';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { keyName as favoriteKeyName } from '@/hooks/use-favorite.hook';
import { resolveLaboratoryName } from '@/utils/laboratory/laboratory.util';
import { formatAnnouncedSpecialties } from '@/utils/professional/professional-license.util';
import { Branch } from '@/app/(main)/(pages)/_types/branch.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum, AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';

const shareButtonContainer = tv({
  slots: {
    container: ['w-full flex flex-col items-center text-center'],
  },
});

export const formatLink = (slug: string, entityType: EntityTypeEnum) => {
  const linkMap = {
    [EntityTypeEnum.PROFESSIONAL]: `${config.env.baseUrl}/profissionais/${slug}/`,
    [EntityTypeEnum.CLINIC]: `${config.env.baseUrl}/parceiros/${slug}/`,
    [EntityTypeEnum.LABORATORY_COLLECT_POINT]: `${config.env.baseUrl}/laboratorios/${slug}/`,
    [EntityTypeEnum.BRANCH]: `${config.env.baseUrl}/unidade/${slug}/`,
  };
  return linkMap[entityType] ?? '';
};

export interface ShareButtonProps extends VariantProps<typeof shareButtonContainer> {
  className?: string;
  dataTestID?: string;
  entityType: EntityTypeEnum;
  entity: Professional | Partner | Laboratory | Branch;
  alwaysShowLargeButton?: boolean;
  renderTrigger?: (openSheet: () => void) => ReactNode;
}

export const generateProfessionalWhatsAppText = (entity: Professional) => {
  const specialties = formatAnnouncedSpecialties(entity);

  return (
    'Aqui está minha indicação para você:\n'
    + `Profissional: *${entity?.honorific ? entity.honorific : ''} ${entity.name}*\n${
      specialties.length > 0 ? `Especialidades: *${specialties.join(', ')}*\n` : ''
    }Agende uma consulta com este profissional pela *Medprev*: ${config.env.baseUrl}/profissionais/${entity.slug}/`
  );
};

const generateClinicWhatsAppText = (entity: Partner) => {
  const clinicName = formatToTitleCase((entity.socialName || entity.legalName) as string);

  return (
    'Aqui está minha indicação para você:\n'
    + `Local: *${clinicName}*\n`
    + `Endereço: *${entity.facilities?.[0]?.neighborhood ?? ''} • ${entity.facilities?.[0]?.city ?? ''} - ${entity.facilities?.[0]?.state ?? ''}*\n`
    + `Agende uma consulta neste local pela *Medprev*: ${config.env.baseUrl}/parceiros/${entity.facilities?.[0]?.slug ?? ''}/`
  );
};

const generateLaboratoryWhatsAppText = (entity: Laboratory) => {
  const laboratoryName = [resolveLaboratoryName(entity), formatToTitleCase(entity.neighborhood as string)].join(' - ');

  return (
    'Aqui está minha indicação para você:\n'
    + `Local: *${laboratoryName}*\n`
    + `Endereço: *${entity.neighborhood} • ${entity.city} - ${entity.state}*\n`
    + `Agende uma consulta neste local pela *Medprev*: ${config.env.baseUrl}/laboratorios/${entity.slug}/`
  );
};

const generateBranchWhatsAppText = (entity: Branch) => {
  return (
    'Aqui está minha indicação para você:\n'
    + `Local: *Medprev ${entity.socialName}*\n`
    + `Endereço: *${entity.address.neighborhood} • ${entity.address.city} - ${entity.address.state}*\n`
    + `Agende uma consulta neste local pela *Medprev*: ${config.env.baseUrl}/laboratorios/${entity.slug}/`
  );
};

const generateWhatsAppText = (
  entityType: EntityTypeEnum,
  entity: Professional | Partner | Laboratory | Branch,
) => {
  switch (entityType) {
    case EntityTypeEnum.PROFESSIONAL:
      return generateProfessionalWhatsAppText(entity as Professional);
    case EntityTypeEnum.CLINIC:
      return generateClinicWhatsAppText(entity as Partner);
    case EntityTypeEnum.LABORATORY_COLLECT_POINT:
      return generateLaboratoryWhatsAppText(entity as Laboratory);
    case EntityTypeEnum.BRANCH:
      return generateBranchWhatsAppText(entity as Branch);
    default:
      return '';
  }
};

export const ShareButton = forwardRef<HTMLDivElement, ShareButtonProps>(
  ({ dataTestID, className, entityType, entity, alwaysShowLargeButton = false, renderTrigger }, ref) => {
    const { container } = shareButtonContainer();
    const [open, setOpen] = useState(false);
    const { toast } = useToast();
    const analytics = getAnalyticsPlugin();

    const getPartnerNameByType = (entityType: Partial<EntityTypeEnum>, entity) => {
      const entityNameByType: Partial<Record<EntityTypeEnum, string>> = {
        [EntityTypeEnum.PROFESSIONAL]: entity?.name,
        [EntityTypeEnum.CLINIC]: entity?.socialName,
        [EntityTypeEnum.LABORATORY_COLLECT_POINT]: `${entity?.main?.socialName} - ${entity?.name}`,
      };

      return entityNameByType[entityType];
    };

    const getShareProfileEventByEntityType = (
      entityType: EntityTypeEnum,
    ): AnalyticsEnumKeyType | undefined => {
      const eventByEntityType: Partial<Record<EntityTypeEnum, AnalyticsEnumKeyType>> = {
        [EntityTypeEnum.PROFESSIONAL]:
      AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_SHARE_PROFILE,

        [EntityTypeEnum.CLINIC]:
      AnalyticsEnum.CLICK_PARTNER_SHARE_PROFILE,

        [EntityTypeEnum.LABORATORY_COLLECT_POINT]:
      AnalyticsEnum.LABORATORY_PROFILE_CLICK_SHARE_PROFILE,
      };

      return eventByEntityType[entityType];
    };

    const trackShareProfile = (
      entityType: EntityTypeEnum,
      entity: Professional | Partner | Laboratory | Branch,
      sharingMethod: string,
    ) => {
      const entityName = getPartnerNameByType(entityType, entity);
      const analyticsName = getShareProfileEventByEntityType(entityType);

      if (entityName && analyticsName) {
        analytics.track(analyticsName, {
          parceiro_id: entity.id,
          parceiro_nome: entityName,
          meio_compartilhamento: sharingMethod,
        });
      }
    };

    const onWhatsappShare = () => {
      const text = generateWhatsAppText(entityType, entity);
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');

      trackShareProfile(
        entityType,
        entity,
        'whatsapp',
      );
    };

    const onCopyLink = () => {
      const slug = 'facilities' in entity ? entity?.facilities?.[0]?.slug : entity?.slug;
      const link = formatLink(slug as string, entityType);
      navigator.clipboard.writeText(link);
      toast.success('Link copiado.');

      trackShareProfile(
        entityType,
        entity,
        'compartilhou link',
      );
    };

    return (
      <>
        <div ref={ref} className={container(className)}>
          {renderTrigger && renderTrigger(() => setOpen(true))}

          {!renderTrigger && (alwaysShowLargeButton
            ? (
                <Button
                  variant="secondary"
                  onClick={() => setOpen(true)}
                  className="rounded-lg w-full h-fit"
                  data-testid={DataTestIdEnum.SHARED_ENTITY_LARGE_TEXT_CARD}
                >
                  <Icon icon={faShare} className="text-xl" />

                  <span className="text-green-500 font-semibold ld text-lg">Compartilhar perfil</span>
                </Button>
              )
            : (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => setOpen(true)}
                    className="rounded-full w-16 h-16 xsm:rounded-lg xsm:w-12 xsm:h-12 xl:w-full xl:h-fit"
                    data-testid={DataTestIdEnum.SHARED_ENTITY_CARD}
                  >
                    <Icon icon={faShare} className="text-xl" />

                    <span className="hidden xl:block text-green-500 font-semibold ld text-lg">Compartilhar perfil</span>
                  </Button>

                  <span className="text-green-400 text-center xsm:hidden mt-2">
                    Compartilhar
                    {' '}
                    <br />
                    {' '}
                    perfil
                  </span>
                </>
              ))}
        </div>
        <Sheet open={open} onOpenChange={setOpen} data-testid={dataTestID}>
          <SheetContent side="centerFit" closingTagPosition="right">
            <SheetHeader>
              <SheetTitle
                className="text-gray-900 font-bold text-2xl text-start"
                data-testid={DataTestIdEnum.SHARED_ENTITY_CARD_TITLE}
              >
                Compartilhar perfil
              </SheetTitle>
              <SheetDescription className="text-gray-800 text-base !mt-4 text-start">
                Convide seus amigos e familiares a conhecerem este perfil.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6">
              <Button
                variant="secondary"
                className="mb-4"
                onClick={onWhatsappShare}
                data-testid={`${DataTestIdEnum.SHARED_ENTITY_WHATSAPP_BUTTON}-${favoriteKeyName[entityType]}`}
              >
                <Icon icon={faWhatsapp} size="1x" className="text-xl" />
                WhatsApp
              </Button>
              <Button
                variant="secondary"
                onClick={onCopyLink}
                data-testid={`${DataTestIdEnum.SHARED_ENTITY_COPY_LINK_BUTTON}-${favoriteKeyName[entityType]}`}
              >
                <Icon icon={faCopy} size="1x" className="text-xl" />
                Copiar link
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  },
);

ShareButton.displayName = 'ShareButton';
